import { useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { collection, getDocs, query, where } from "firebase/firestore";
import { dcContent } from "../../services/content";
import { JobSection } from "../../src/components/JobSection";
import { Box, Container, Typography } from "@mui/material";
import { NextSeo } from "next-seo";
import { OneJobBtn } from "../../src/components/JobBtn";
import { db } from "../../api_config/firebase-configuration";
import { JobNotFound } from "../../src/components/JobNotFound";
import {
  LocalizationContext,
  PlaceContext,
  ShareLinkObject,
} from "../../src/lib/context";
import ShareButton from "../../src/components/ShareButton";
import theme from "../../src/styles/theme";
import { useUserData } from "../../src/hooks/useUserData";
import { ContentProvider } from "../../services/content/contentProvider";
import { GetStaticPaths } from "next";
import PromotionalCard from "src/components/CommonComponents/PromotionalCard";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import BottomInfo from "src/components/BottomInfo";
import GoogleMapReact from "google-map-react";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import CategorizedJobList from "src/components/Sections/CategorizedJobList";
import Carousel from "react-material-ui-carousel";
import Image from "next/image";

const singleJobQueryBody = [
  {
    fields: [
      "id",
      "title",
      "description",
      "place",
      "exampleImages { url, alt }",
      "videolink",
      "projectType",
      "offerStart",
      "offerDeadline",
      "offerCurrency",
      "uploadDeadline",
      {
        name: "typeSpecBlock",
        fields: [
          {
            name: "... on ProjtypeOtherRecord",
            fields: [
              "id",
              "scenario",
              {
                name: "jobs",
                fields: [
                  "id",
                  "height",
                  "duration",
                  "contentType",
                  "rtk",
                  "quantity",
                ],
              },
            ],
          },
          {
            name: "... on ProjtypeCreativeRecord",
            fields: ["id", "mediaType", "periodEnd", "periodStart", "quality"],
          },
          {
            name: "... on ProjtypeAgricultRecord",
            fields: [
              "id",
              "agrType",
              "areaSize",
              "areaSizeUnit",
              "periodStart",
              "periodEnd",
              "plantType",
              { name: "location", fields: ["longitude", "latitude"] },
            ],
          },
          {
            name: "... on ProjtypeSurvmapRecord",
            fields: [
              "id",
              "areaSize",
              "areaSizeUnit",
              "dimension",
              "evalType",
              "periodStart",
              "periodEnd",
            ],
          },
        ],
      },
    ],
  },
];

export const getStaticPaths: GetStaticPaths = async () => {
  const queryName = "allTenders";
  const jobsContentProvider = new ContentProvider("jobs");
  const jobsQuery = jobsContentProvider.generateQuery(
    queryName,
    [{ fields: ["id"] }],
    {
      first: 100,
    }
  );
  let jobs;
  try {
    jobs = await jobsContentProvider.request(jobsQuery, queryName);
  } catch (e) {
    console.error(e);
  }
  const paths = jobs?.map((tender: { id: string }) => ({
    params: { job: tender.id },
  })) || [{ params: { job: "" } }];
  return { paths, fallback: "blocking" };
};

export const getStaticProps = async ({
  params,
  locale,
  defaultLocale,
}: {
  params: { job: string };
  locale: string;
  defaultLocale: string;
}) => {
  const tenderQueryName = "tender";
  const jobsContentProvider = new ContentProvider("jobs");
  const jobQuery = jobsContentProvider.generateQuery(
    tenderQueryName,
    singleJobQueryBody,
    {
      filter: `{id: {eq: "${params.job}"}}`,
      locale: locale,
      fallbackLocales: defaultLocale,
    }
  );
  const jobsQueryName = "allTenders";
  const jobsQuery = jobsContentProvider.generateQuery(
    jobsQueryName,
    [
      {
        fields: [
          "id",
          "title",
          "country",
          "place",
          "projectType",
          "exampleImages {url}",
          "offerStart",
          "offerDeadline",
          "uploadDeadline",
        ],
      },
    ],
    { orderBy: "offerDeadline_ASC", fallbackLocales: "en", first: 100 }
  );
  let job: any;
  try {
    job = await jobsContentProvider.request(jobQuery, tenderQueryName);
  } catch (e) {
    return { props: { job: null } };
  }

  const jobs = await jobsContentProvider.request(jobsQuery, jobsQueryName);
  const filteredJobs = jobs.filter(
    (item: any) => item?.projectType === job?.projectType
  );
  return {
    props: { job, filteredJobs },
    revalidate: 7200,
  };
};

const deadlineBoxStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  border: "1px solid",
  borderColor: "textColors.light",
  padding: "33px 50px",
  [theme.breakpoints.down("lg")]: {
    padding: { md: "6% 8%", sm: "6% 13%", xs: "6% 5%" },
  },
};

export default function Job({
  job,
  filteredJobs,
}: {
  job: any;
  filteredJobs: any[];
}) {
  const { jobPage, jobsPage, uiGeneral, errorPage } =
    useContext(LocalizationContext).localeStrings;
  const discountsPromoStrings = uiGeneral.promoCards.filter(
    (c) => c.promoId === "discounts"
  )[0];
  const bottomInfoTexts = [
    jobsPage.aboutBody1,
    jobsPage.aboutBody2,
    jobsPage.aboutBody3,
    jobsPage.aboutBody4,
    jobsPage.aboutBody5,
    jobsPage.aboutBody6,
  ];
  const { actDroner } = useUserData();
  const { isFallback, push } = useRouter();
  const [dronerTenders, setDronerTenders] = useState([]);
  const { shareLinkObject, setShareLinkObject } = useContext(ShareLinkObject);
  const [center, setCenter] = useState<any>({ lat: 50.3785, lng: 14.9706 });
  const { place, setPlace } = useContext(PlaceContext);
  const [actZoom, setActZoom] = useState(3);
  const [actPosition, setActPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const defaultZoom = actZoom;

  useEffect(() => {
    setCenter(null);
    !!actPosition && setCenter(actPosition);
    setActZoom(4);
  }, [actPosition]);

  useEffect(() => {
    !!place && setCenter({ ...place });
    setActZoom(4);
  }, [place]);

  useEffect(() => {
    setShareLinkObject({
      title: job?.title,
      url: window.location.href,
    });
  }, [job?.title, setShareLinkObject]);

  useEffect(() => {
    if (actDroner) {
      const temp: any = [];
      const dronerTenderQuery = query(
        collection(db, "offers"),
        where("uid", "==", actDroner.uid),
        where("tender", "==", job?.id || "")
      );
      getDocs(dronerTenderQuery)
        .then((snap) => {
          snap.forEach((doc) => {
            temp.push(doc.data());
          });
          setDronerTenders(temp);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [actDroner, job?.id]);

  if (isFallback) {
    return <div>{dcContent.text.loading}</div>;
  }
  if (!job) {
    return <JobNotFound />;
  } else {
    return (
      <>
        <NextSeo
          title={jobPage.title + job?.title}
          description={job.description}
          openGraph={{
            type: "website",
            title: jobPage.title + job?.title,
            description: job.description,
            images: [
              {
                url:
                  job?.exampleImages[0]?.url ?? "/assets/index_thumbnail.webp",
                alt: job?.exampleImages[0]?.alt,
              },
            ],
          }}
          twitter={{
            handle: "@handle",
            site: "@site",
            cardType: "summary_large_image",
          }}
        />
        <Box>
          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 6,
              pt: 10,
              maxWidth: "1248px !important",
              marginBottom: "60px",
            }}
          >
            <div className="flex align-items-center">
              <Typography
                onClick={() => push("/jobs")}
                sx={{ textDecoration: "underline", cursor: "pointer" }}
              >
                All drone jobs
              </Typography>
            </div>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(min-content, 58%) minmax(min-content, 1fr)",
                columnGap: "3vw",
                rowGap: "1.5vw",
              }}
            >
              {/* Title */}
              <Box
                sx={{
                  [theme.breakpoints.down("md")]: {
                    gridColumn: "1 / -1",
                    gridRow: "1 / 2",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    // alignItems: "center",
                    marginBottom: "20px",
                  },
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    [theme.breakpoints.down("lg")]: {
                      margin: "0",
                      fontSize: { md: "3.5rem", sm: "2.5rem" },
                    },
                  }}
                >
                  {job?.title}
                </Typography>
                <Box className="flex mt-small">
                  <a href="mailto:">
                    <ShareButton linkObj={shareLinkObject} />
                  </a>
                  <div className="flex gap-normal ml-large align-items-center"></div>
                </Box>
              </Box>

              {/* Deadline Boxes */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  [theme.breakpoints.down("md")]: {
                    fontSize: 9,
                    gridColumn: "1 / -1",
                    margin: "2rem 0",
                  },
                }}
              >
                <Box
                  className="bg-surface rounded-all-normal text-center"
                  sx={deadlineBoxStyle}
                >
                  <Typography
                    fontWeight={600}
                    sx={{
                      [theme.breakpoints.down("md")]: {
                        fontSize: "1rem",
                      },
                      color: "textColors.light",
                      fontWeight: "bold",
                    }}
                  >
                    {jobPage?.offerDeadline}
                  </Typography>
                  <Typography
                    sx={{
                      color: "secondary.main",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                    }}
                  >
                    {job?.offerDeadline.replaceAll("-", ".")}
                  </Typography>
                </Box>
                <Box
                  className="bg-surface rounded-all-normal text-center"
                  sx={deadlineBoxStyle}
                >
                  <Typography
                    fontWeight={600}
                    sx={{
                      [theme.breakpoints.down("md")]: {
                        fontSize: "1rem",
                      },
                      color: "textColors.light",
                      fontWeight: "bold",
                    }}
                  >
                    {jobPage.projectDeadline}
                  </Typography>
                  <Typography
                    sx={{
                      color: "secondary.main",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                    }}
                  >
                    {job?.uploadDeadline.replaceAll("-", ".")}
                  </Typography>
                </Box>
              </Box>

              {/* Description & Place */}
              <Box
                sx={{
                  [theme.breakpoints.down("md")]: {
                    gridColumn: "1 / -1",
                  },
                }}
              >
                {/* Description */}
                <JobSection
                  title={jobPage.descriptionSectionTitle}
                  text={job.description}
                  icon={<DescriptionOutlinedIcon />}
                  keepBreaks={true}
                />

                {/* Place */}
                <JobSection
                  title={jobPage.placeSectionTitle}
                  text={job.place}
                  icon={<FmdGoodOutlinedIcon />}
                  className="mt-large"
                />
              </Box>

              {/* Map & Have a question */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  [theme.breakpoints.down("md")]: {
                    gridColumn: "1 / -1",
                  },
                }}
              >
                <Box
                  sx={{
                    height: { md: "230px", sm: "25vh", xs: "19vh" },
                    marginBottom: { md: 0, xs: "2rem" },
                  }}
                >
                  <GoogleMapReact
                    yesIWantToUseGoogleMapApiInternals
                    bootstrapURLKeys={{
                      key: process.env.NEXT_PUBLIC_MAPS_API_KEY as string,
                    }}
                    defaultZoom={defaultZoom}
                    center={center}
                    onChange={(e: any) => {
                      setActZoom(e.zoom);
                    }}
                  />
                </Box>
                {/* Have a question */}
                <Box sx={{ textAlign: "center" }}>
                  <Image
                    src="/assets/email.png"
                    alt=""
                    width={15}
                    height={12}
                  />
                  <a
                    href={`mailto:${errorPage?.mailToLink}`}
                    className="settings__underLineText"
                    children={uiGeneral.contactUs2}
                    style={{ marginLeft: "10px" }}
                  />
                </Box>
              </Box>

              {/* Work button */}
              <Box
                sx={{
                  [theme.breakpoints.down("md")]: {
                    gridColumn: "1 / -1",
                    gridRow: "2 / 3",
                  },
                }}
              >
                <OneJobBtn job={job} dronerTenders={dronerTenders} />
              </Box>

              {/* Job Images */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,

                  [theme.breakpoints.down("md")]: {
                    justifyContent: "space-between",
                    gridColumn: "1 / -1",
                    gridRow: "3 / 4",
                  },
                  [theme.breakpoints.down("sm")]: {
                    display: "none",
                  },
                }}
              >
                {job.exampleImages.slice(0, 3).map(
                  (
                    image: {
                      url: any;
                      alt: string | undefined;
                    },
                    i: number
                  ) => {
                    return (
                      <Box key={i} className="w-full">
                        <Box
                          className="relative"
                          sx={{
                            height: 144,
                            width: 144,
                            [theme.breakpoints.down("md")]: {
                              height: 247,
                              width: "100%",
                            },
                          }}
                        >
                          <Image
                            sizes="(min-width: 200px) 200px,
                                    (min-height: 200px) 200px,
                                   200px"
                            layout="fill"
                            objectFit="cover"
                            src={image.url}
                            alt={image.alt as string}
                            className="rounded-all-normal"
                          />
                        </Box>
                      </Box>
                    );
                  }
                )}
              </Box>
              {job.exampleImages.length > 1 ? (
                <Carousel
                  navButtonsAlwaysVisible
                  sx={{
                    display: { sm: "none" },
                    [theme.breakpoints.down("sm")]: {
                      gridColumn: "1 / -1",
                      gridRow: "3 / 4",
                      display: "block",
                    },
                  }}
                >
                  {job.exampleImages.map(
                    (
                      image: {
                        url: any;
                        alt: string | undefined;
                      },
                      i: number
                    ) => (
                      <Box key={i} className="w-full">
                        <Box
                          className="relative"
                          sx={{
                            height: 144,
                            width: 144,
                            [theme.breakpoints.down("md")]: {
                              height: 247,
                              width: "100%",
                            },
                          }}
                        >
                          <Image
                            sizes="(min-width: 200px) 200px,
                                      (min-height: 200px) 200px,
                                     200px"
                            layout="fill"
                            objectFit="cover"
                            src={image.url}
                            alt={image.alt as string}
                            className="rounded-all-normal"
                          />
                        </Box>
                      </Box>
                    )
                  )}
                </Carousel>
              ) : (
                <Box
                  sx={{
                    gap: 2,
                    display: { sm: "none" },
                    [theme.breakpoints.down("md")]: {
                      justifyContent: "space-between",
                      gridColumn: "1 / -1",
                      gridRow: "3 / 4",
                    },
                  }}
                >
                  {job.exampleImages.map(
                    (
                      image: {
                        url: any;
                        alt: string | undefined;
                      },
                      i: number
                    ) => (
                      <Box key={i} className="w-full">
                        <Box
                          className="relative"
                          sx={{
                            height: 144,
                            width: 144,
                            [theme.breakpoints.down("md")]: {
                              height: 247,
                              width: "100%",
                            },
                          }}
                        >
                          <Image
                            sizes="(min-width: 200px) 200px,
                                    (min-height: 200px) 200px,
                                   200px"
                            layout="fill"
                            objectFit="cover"
                            src={image.url}
                            alt={image.alt as string}
                            className="rounded-all-normal"
                          />
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              )}
            </Box>

            <CategorizedJobList
              title={jobPage.otherJobs}
              type={job?.projectType}
              jobs={filteredJobs}
            />

            <PromotionalCard
              title={discountsPromoStrings.title}
              content={discountsPromoStrings.description}
              imgUrl="/assets/discounts_cover_img.webp"
              btnContent={discountsPromoStrings.buttonText}
              link="/discounts"
              left
              dark
            />
          </Container>

          <BottomInfo
            headerText={jobsPage.aboutTitle}
            textsArray={bottomInfoTexts}
            sendEmail={true}
            email={errorPage?.mailToLink}
          />
        </Box>
      </>
    );
  }
}
